import React from 'react';
import { FormInstance } from 'antd';
import { Modal } from 'antd';
import { errorManipulation } from './error-manipulation';

interface ErrorCollection {
    [key: string]: string[];
}

/**
 * Helper function to determine which wizard step a field belongs to
 * This is used for multi-step forms to navigate to the step with errors
 */
export function getStepForField(fieldName: string): number {
    // Step 0: Drug Information
    if (
        [
            'medication_name',
            'release_form',
            'international_name_id',
            'inn',
        ].includes(fieldName)
    ) {
        return 0;
    }

    // Step 1: Certificate and Procedure
    if (
        [
            'certificate',
            'procedure_id',
            'procedure_desc',
            'applicant_id',
            'notification_email',
        ].includes(fieldName)
    ) {
        return 1;
    }

    // Step 2: General Information
    if (
        [
            'is_electronic_form',
            'is_mibp',
            'is_ectd',
            'application_notes',
        ].includes(fieldName)
    ) {
        return 2;
    }

    // Step 3: Documents
    if (['files', 'files_count'].includes(fieldName)) {
        return 3;
    }

    // Step 4: Submission/Review
    if (['reg_date'].includes(fieldName)) {
        return 4;
    }

    // Default to step 0 if field not found
    return 0;
}

interface HandleFormErrorParams {
    e: any;
    form: FormInstance;
    setIsFormProcessing: (val: boolean) => void;
    setDisSubmit: (val: boolean) => void;
    msgError: (msg: string) => void;
    t: (key: string) => string; // або як у тебе типізована функція перекладу
    redirectToErrorScreen?: () => void; // Optional callback for custom error screen redirect
    navigateToStep?: (step: number) => void; // Optional callback to navigate to wizard step with error
}

export interface FormErrorField {
    name: (string | number)[];
    errors: string[];
}

export type FormErrorFields = FormErrorField[] | undefined;

/**
 * Universal error handler for form submissions
 * Handles both validation errors (422) and server errors (5xx, network issues)
 *
 * @param params - Error handling parameters
 * @returns Form error fields if validation errors exist
 */
export function handleFormError({
    e,
    form,
    setIsFormProcessing,
    setDisSubmit,
    msgError,
    t,
    redirectToErrorScreen,
    navigateToStep,
}: HandleFormErrorParams): FormErrorFields {
    console.error('Form submission error:', e);

    const status = e?.response?.status;
    const errorMessage = e?.response?.data?.message || e?.message;

    // Check if this is a validation error (422)
    const errCollection = e?.response?.data?.errors as
        | ErrorCollection
        | undefined;

    let errFields: FormErrorField[] | undefined;

    // Handle validation errors (422 - Unprocessable Entity)
    if (status === 422 && errCollection) {
        // Перетворюємо об'єкт на масив "errFields"
        errFields = Object.entries(errCollection).map(([key, msgs]) => {
            const manipulatedErrors = errorManipulation(msgs);

            // Safety check: if errorManipulation returns empty array or empty strings,
            // use the original messages as fallback
            const safeErrors =
                manipulatedErrors.length > 0 &&
                manipulatedErrors.some(err => err && err.trim().length > 0)
                    ? manipulatedErrors
                    : msgs; // Fallback to original messages

            return {
                name: key.split('.').map(i => (Number.isInteger(+i) ? +i : i)),
                errors: safeErrors,
            };
        });

        // Log the errors being set for debugging
        console.log('📝 Preparing to set field errors:', errFields);
        errFields.forEach(field => {
            console.log(
                `  - Field "${field.name.join('.')}": ${field.errors.join(', ')}`
            );
        });

        // For wizard forms: Navigate to the step containing the first error FIRST
        if (navigateToStep && errFields && errFields.length > 0) {
            const firstErrorFieldName =
                errFields[0].name[0]?.toString() || '';
            const targetStep = getStepForField(firstErrorFieldName);
            const fieldsToSet = errFields; // Capture in const for setTimeout

            console.log(
                `🔍 First error field: "${firstErrorFieldName}" is on step ${targetStep}`
            );

            // Navigate to the step with the error FIRST
            navigateToStep(targetStep);

            // THEN set errors after navigation completes and fields are mounted
            setTimeout(() => {
                console.log('✅ Setting errors on mounted fields');
                form.setFields(fieldsToSet);

                // Scroll to the first error field after errors are set
                const firstErrorField = fieldsToSet[0];
                if (firstErrorField) {
                    setTimeout(() => {
                        form.scrollToField(firstErrorField.name, {
                            behavior: 'smooth',
                            block: 'center',
                        });
                    }, 100);
                }
            }, 150); // Wait for step transition and field mounting
        } else {
            // No navigation needed, set errors immediately
            form.setFields(errFields);
        }

        // Show validation error message
        msgError(t('notifications.entry_error'));
    }
    // Handle server errors (5xx, network errors, etc.)
    else {
        const isServerError = !status || status >= 500;
        const isNetworkError = !e?.response;

        // Show detailed error modal for server errors
        if (isServerError || isNetworkError) {
            const defaultTitle = 'Помилка сервера';
            const defaultDescription =
                'Виникла помилка на сервері. Будь ласка, спробуйте пізніше або зверніться до служби підтримки.';
            const defaultOkText = 'Зрозуміло';

            Modal.error({
                title: t('notifications.server_error_title') || defaultTitle,
                content: (
                    <div>
                        <p>
                            {t('notifications.server_error_description') ||
                                defaultDescription}
                        </p>
                        {errorMessage && (
                            <div
                                style={{
                                    marginTop: '12px',
                                    padding: '8px',
                                    background: '#f5f5f5',
                                    borderRadius: '4px',
                                }}
                            >
                                <strong>Деталі помилки:</strong>
                                <div
                                    style={{
                                        marginTop: '4px',
                                        fontSize: '12px',
                                        color: '#666',
                                    }}
                                >
                                    {errorMessage}
                                </div>
                            </div>
                        )}
                        {status && (
                            <div
                                style={{
                                    marginTop: '8px',
                                    fontSize: '12px',
                                    color: '#999',
                                }}
                            >
                                Код помилки: {status}
                            </div>
                        )}
                    </div>
                ),
                okText: t('buttons.ok') || defaultOkText,
                onOk: () => {
                    // If custom redirect is provided, use it
                    if (redirectToErrorScreen) {
                        redirectToErrorScreen();
                    }
                },
            });
        } else {
            // Handle other errors (4xx except 422)
            msgError(errorMessage || t('notifications.entry_error'));
        }
    }

    // Скидаємо спінери й не даємо кнопці бути disabled
    setIsFormProcessing(false);
    setDisSubmit(false);

    return errFields;
}
